/*
* Main app bootstrapping & react-router routing configuration
*/

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as en from 'react-intl/locale-data/en';
import DocumentTitle from 'react-document-title';
import createHistory from 'history/createBrowserHistory';
import { ConnectedRouter, routerMiddleware } from 'react-router-redux';
import { IntlProvider } from 'react-intl-redux';
import { Provider } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Route, Switch } from 'react-router';
import { addLocaleData } from 'react-intl';
import { applyMiddleware, createStore } from 'redux';
import { logger as reduxLogger } from 'redux-logger';
import { queryMiddleware } from 'redux-query';
import 'hammerjs';

// App layout, auth, navigation, branding
import App from './App';
import AppRootHome from './app-root-home';
import ActivateAccount from './activate-account';
import ChangePassword from './user-change-password';
import Branding from './branding/branding';
import LoginCallback from './login/callback';
import loadingBar from './middleware/loadingBar';
import { AuthorizedRoute } from './common/routing';
import { NavigationLinks } from './common/navigation';
import { authInterceptor } from './middleware/authInterceptor';

// App views, components

import AddAssociation from './association/add';
import AddClient from './client/add';
// import AddOrder from './order/add';
import AddOrderBid from './order/add-bid';
import AddUser from './user/add';
import Association from './association';
import AssociationInventory from './inventory';
import AssociationInventoryDetail from './inventory/detail';
import AssociationPropertyReportPdf from './association/property-report/pdf';
import CaliforniaDisclosureReportPdf from './report/pdf/ca-disclosure-pdf';
// import AssociationClientQuestionsReportPdf from './association/client-questions-report/pdf';
import AssociationSearch from './association-search';
import AssociationViewOrder from './association/view-order';
import Client from './client';
import Logout from './app-root-home/logout';
import ClientDashboard from './client-dashboard';
// import QueryDB from './querydb';
import ClientSearch from './client-search';
import ClientContactsSearch from './client-search/client-contacts-search';
import CoordinatorJobsSearch from './coordinator-jobs-search';
import CreateInvite from './invite/create-invite';
// import CreateBid from './bid/create-bid';
import Dashboard from './dashboard';
import PreparedBidsSearch from './dashboard/prepared-bids';
import SendBidsSearch from './dashboard/send-bids';
import AssociationExportOrders from './dashboard/association-export-orders';
import DefaultManagers from './default-managers';
import DefaultManagersEdit from './default-managers/edit';
import EditAssociation from './association/edit';
import EditClient from './client/edit';
import ComponentDetail from './component';
import EditLookupTable from './lookup-tables/edit';
import EditTextContent from './text-content/edit';
import EditChildLookupTable from './lookup-tables/edit-child';
import EditOrder from './order/edit';
import EditUser from './user/edit';
import LookupTables from './lookup-tables';
import MyAccount from './my-account';
// import NewResearchBids from './new-research-bids';
import Order from './order';
import OrderBidEnvelopePdf from './order/bid-envelope/pdf';
import OrderBidEnvelopeMultiplePdf from './order/bid-envelope/pdf-multiple';
import OrderBidLetterPdf from './order/bid-letter/pdf';
import OrderBidLetterMultiplePdf from './order/bid-letter/pdf-multiple';
import OrderSearch from './order-search';
import EditComponent from './component/edit';
import OutstandingBids from './outstanding-bids';
import ReminderBids from './reminder-bids';
import ReportAssociation from './report';
import ReportAssociationPdf from './report/pdf/pdf';
import PortalAssociationReport from './report/portal';
import ReportAssociationClientQuestionsPdf from './report/pdf/client-questions-pdf';
import ResearchBids from './research-bids';
import ResearchComponentsSearch from './research-components-search';
import ReserveStudy from './reserve-study';
import UserDetail from './user';
import UserInventoryTemplate from './user-inventory-template';
import UserSearch from './user-search';
import RegionalComponentListManager from './regional-component-lists';
import RegionalComponentListEdit from './regional-component-lists/edit';
import RegionalComponentListDetail from './regional-component-lists/detail';
import WonJobs from './won-jobs';
import ErrorBoundary from './common/ErrorBoundary';

// import registerServiceWorker from './registerServiceWorker';
import Reducers from './reducers';
import Messages from './config/intl-messages';

// CSS/Styling. Ensure index.css is imported last, so overrides apply correctly
import 'react-select/dist/react-select.css';
import '@progress/kendo-theme-bootstrap/dist/all.css';
// import '@progress/kendo-theme-default/dist/all.css'; 20180630 changed from default to bootstrap theme
import 'react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import 'react-virtualized/styles.css';
import 'react-virtualized-select/styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'rc-slider/assets/index.css';
import './applayout/buttons-css/btn-mint.css';
import './applayout/buttons-css/btn-purple.css';
import './index.css';

// react-router-redux
const history = createHistory();

// App authentication initialization
// const auth = new Auth();

// Branded app text & internationalization setup
addLocaleData([...en]);
const brandKey = Branding.getBrandingKey();
const initialState = {
  branding: Branding.getInitialBrandingState(brandKey),
  intl: Messages.getInitialIntlState(brandKey),
};

// react-redux initialization
const getQueries = (state) => state.queries;
const getEntities = (state) => state.entities;

// note: reduxLogger must be the last middleware in the applyMiddleware arg list
const middleware = applyMiddleware(
  routerMiddleware(history),
  loadingBar({
    hideLoaderPromiseTypeSuffixes: ['REQUEST_SUCCESS', 'REQUEST_FAILURE', 'CANCEL_QUERY'],
    showLoaderPromiseTypeSuffixes: ['REQUEST_START'],
  }),
  queryMiddleware(getQueries, getEntities),
  authInterceptor,
  reduxLogger
);

const store = createStore(Reducers, initialState, middleware);

// TODO: REFACTOR ALL ROUTE BUILDING TO EXTERNAL FILE
// react-router app route definitions
const appRoutesAuthed = props => {

  // Route path builder
  const BuildPath = (route: string) => NavigationLinks.getAppAuthPath(route);
  const RoutePaths = NavigationLinks.AppAuthRoutePaths;
  const getRouteConfigProps = (routePath, componentType, exact = false, requiresRegionalComponentListAccess = false) => {
    return {
      path: BuildPath(routePath),
      component: componentType,
      exact: exact,
      requiresRegionalComponentListAccess,
    };
  };

  const routeConfig = [
    getRouteConfigProps(RoutePaths.HomeDashboard, Dashboard, false),
    getRouteConfigProps(RoutePaths.ClientDashboard.Home, ClientDashboard, false),

    /* Association routes */
    getRouteConfigProps(RoutePaths.Association.Search, AssociationSearch, false),
    getRouteConfigProps(RoutePaths.Association.ExportOrders, AssociationExportOrders, false),
    getRouteConfigProps(RoutePaths.Association.Add, AddAssociation, true),
    getRouteConfigProps(RoutePaths.Association.Detail, Association, true),
    getRouteConfigProps(RoutePaths.Association.Edit, EditAssociation, true),
    getRouteConfigProps(RoutePaths.Association.InventorySubgroup, AssociationInventory, true),
    getRouteConfigProps(RoutePaths.Association.Inventory, AssociationInventory, true),
    getRouteConfigProps(RoutePaths.Association.InventoryDetail, AssociationInventoryDetail, true),
    getRouteConfigProps(RoutePaths.Association.ReserveStudy, ReserveStudy, true),
    getRouteConfigProps(RoutePaths.Association.PropertyReportPdf, AssociationPropertyReportPdf, true),
    getRouteConfigProps(RoutePaths.Association.ViewOrder, AssociationViewOrder, true),

    /* Client Routes */
    getRouteConfigProps(RoutePaths.Client.Search, ClientSearch, false),
    getRouteConfigProps(RoutePaths.Client.SearchContacts, ClientContactsSearch, false),
    getRouteConfigProps(RoutePaths.Client.Add, AddClient, true),
    getRouteConfigProps(RoutePaths.Client.Detail, Client, true),
    getRouteConfigProps(RoutePaths.Client.Edit, EditClient, true),

    /* Order routes */
    getRouteConfigProps(RoutePaths.Order.Search, OrderSearch, false),
    getRouteConfigProps(RoutePaths.Order.Detail, Order, true),
    getRouteConfigProps(RoutePaths.Order.Edit, EditOrder, true),
    getRouteConfigProps(RoutePaths.Order.BidLetterPdf, OrderBidLetterPdf, true),
    getRouteConfigProps(RoutePaths.Order.BidEnvelopePdf, OrderBidEnvelopePdf, true),
    getRouteConfigProps(RoutePaths.Order.AddBid, AddOrderBid, false),

    /* Print template routes */
    getRouteConfigProps(RoutePaths.PrintMulti.BidEnvelopeMuliplePdf, OrderBidEnvelopeMultiplePdf, false),
    getRouteConfigProps(RoutePaths.PrintMulti.BidLetterMuliplePdf, OrderBidLetterMultiplePdf, false),

    /* Component routes */
    getRouteConfigProps(RoutePaths.Component.Detail, ComponentDetail, true),
    getRouteConfigProps(RoutePaths.Component.Edit, EditComponent, true),

    /* Bid routes */
    getRouteConfigProps(RoutePaths.Bids.Outstanding, OutstandingBids, false),
    getRouteConfigProps(RoutePaths.Bids.Reminder, ReminderBids, false),
    getRouteConfigProps(RoutePaths.Bids.Research, ResearchBids, false),
    getRouteConfigProps(RoutePaths.Bids.PreparedBids, PreparedBidsSearch, false),
    getRouteConfigProps(RoutePaths.Bids.SendBids, SendBidsSearch, false),

    /* Job routes */
    getRouteConfigProps(RoutePaths.Jobs.Won, WonJobs, false),
    getRouteConfigProps(RoutePaths.Jobs.Coordinator, CoordinatorJobsSearch, false),

    /* Inventory/Component routes */
    getRouteConfigProps(RoutePaths.ResearchComponents.Search, ResearchComponentsSearch, false),

    /* Report routes */
    getRouteConfigProps(RoutePaths.Reports.ReserveStudy, ReportAssociation, true),
    getRouteConfigProps(RoutePaths.Reports.ReserveStudyPdf, ReportAssociationPdf, true),
    getRouteConfigProps(RoutePaths.Reports.ClientQuestionsPdf, ReportAssociationClientQuestionsPdf, true),
    getRouteConfigProps(RoutePaths.Reports.CaliforniaDisclosurePdf, CaliforniaDisclosureReportPdf, true),
    getRouteConfigProps(RoutePaths.Reports.PortalReserveStudy, PortalAssociationReport, true),

    /* User routes */
    getRouteConfigProps(RoutePaths.Users.Search, UserSearch, false),
    getRouteConfigProps(RoutePaths.Users.Detail, UserDetail, true),
    getRouteConfigProps(RoutePaths.Users.Edit, EditUser, true),
    getRouteConfigProps(RoutePaths.Users.Add, AddUser, true),

    /* Invite routes */
    getRouteConfigProps(RoutePaths.Invite.Add, CreateInvite, true),
    getRouteConfigProps(RoutePaths.Invite.AddForAssociation, CreateInvite, true),

    /* Account routes */
    getRouteConfigProps(RoutePaths.Account.MyAccount, MyAccount, false),

    /* User Inventory Template routes */
    getRouteConfigProps(RoutePaths.UserInventoryTemplates.Edit, UserInventoryTemplate, true),

    /* Regional Component List routes (regional_component_list_manager role) */
    getRouteConfigProps(RoutePaths.RegionalComponentLists.Manager, RegionalComponentListManager, false, true),
    getRouteConfigProps(RoutePaths.RegionalComponentLists.Edit, RegionalComponentListEdit, true, true),
    getRouteConfigProps(RoutePaths.RegionalComponentLists.Detail, RegionalComponentListDetail, true, true),

    /* Administration routes */
    getRouteConfigProps(RoutePaths.Administration.DefaultManagers, DefaultManagers, false),
    getRouteConfigProps(RoutePaths.Administration.RegionEdit, DefaultManagersEdit, true),
    getRouteConfigProps(RoutePaths.Administration.TextContentEdit, EditTextContent, true),
    getRouteConfigProps(RoutePaths.Administration.LookupTables, LookupTables, true),
    getRouteConfigProps(RoutePaths.Administration.LookupTableEdit, EditLookupTable, true),
    getRouteConfigProps(RoutePaths.Administration.LookupTableEditChildObj, EditChildLookupTable, true),
    // getRouteConfigProps(RoutePaths.Administration.QueryDB, QueryDB, true),
  ];

  // Iterate routes, build AuthorizedRoute tags
  const routes = routeConfig.map((routeProps, i) => (
    <AuthorizedRoute key={i} {...routeProps} />
  ));

  // Render Switch with generated Authorized routes
  return (
      <Switch>
        {routes}

        <Redirect to={BuildPath(RoutePaths.HomeDashboard)} />
      </Switch>
  );
};

const appRoutes = (
  <ErrorBoundary>
    <div>    
      <Route component={App} />
      <Switch>
          <Route exact={true} path="/" component={AppRootHome} />
          <Route path={NavigationLinks.AppAuthRoutePaths.Logout.Logout} component={Logout} />

          <Route
              path={NavigationLinks.loginCallback()}
              render={(props) => {
                return <LoginCallback {...props} />;
              }}
          />
          <Route path={NavigationLinks.AppAuthRoutePaths.ChangePassword.ChangePassword} component={ChangePassword} />
          <Route path={NavigationLinks.AppAuthRoutePaths.AccountActivation.ActivateAccount} component={ActivateAccount} />

          <AuthorizedRoute
              path={NavigationLinks.AppAuthBasePath}
              component={appRoutesAuthed}
              requiresRegionalComponentListAccess={false}
          />
      </Switch>
    </div>
  </ErrorBoundary>
);

// Render application root
ReactDOM.render(
  <Provider store={store}>
    <IntlProvider>
      <DocumentTitle title={initialState.branding.companyName}>
        <ConnectedRouter history={history}>          
           {appRoutes}
        </ConnectedRouter>
      </DocumentTitle>
    </IntlProvider>
  </Provider>,
  document.getElementById('root') as HTMLElement
);

// registerServiceWorker();



// WEBPACK FOOTER //
// ./src/index.tsx